import React, { useMemo } from 'react'
import ConditionalRender from 'utils/conditionalRender'
import { StaticTableData, StaticTableHeader, StaticTableRow } from './styles'
import { useTable, usePagination, useSortBy } from 'react-table'
import { useSTheme } from 'styles/theme'
import { FParagraph } from '../Text'
import { useTranslation } from 'i18n'

interface Props {
    tableData: any[]
    headerColor?: string
}

const FStaticTable: React.FC<Props> = (props: Props) => {
    const { tableData } = props
    const pageSize = 14
    const theme = useSTheme()
    const { t } = useTranslation(window.LANG_NS)
    const fontFamily = theme.fontsV2[window.ORGANIZATION.name]?.body || theme.fontsV2.default

    function capitalizeFirstLetter(string) {
        return string.charAt(0).toUpperCase() + string.slice(1)
    }

    const columns = useMemo(() => {
        const cols = {}
        tableData.forEach((item) => {
            Object.keys(item).forEach((key) => {
                cols[key] = cols[key] || capitalizeFirstLetter(key.replaceAll('_', ' '))
            })
        })

        return Object.keys(cols).map((c) => {
            return {
                Header: cols[c],
                accessor: c,
            }
        })
    }, [tableData])

    const { getTableProps, getTableBodyProps, headerGroups, prepareRow, page } = useTable(
        {
            columns,
            data: tableData || [],
            initialState: { pageIndex: 0, pageSize },
            autoResetSortBy: false,
            autoResetPage: false,
        },
        useSortBy,
        usePagination
    )

    return (
        <table {...getTableProps()} width={'100%'} style={{ borderSpacing: '0px', overflow: 'hidden' }}>
            <ConditionalRender if={!tableData?.length}>
                <FParagraph text={t('ssi_no_data_to_display')} textStyles={{ marginTop: '0px' }} />
            </ConditionalRender>
            <ConditionalRender if={tableData?.length}>
                <thead
                    style={{
                        background: props.headerColor,
                        borderRadius: '8px',
                        marginBottom: '5px',
                        borderCollapse: 'separate',
                        overflow: 'hidden',
                    }}
                >
                    {headerGroups.map((headerGroup, index) => (
                        <tr key={index} {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column, index) => (
                                <StaticTableHeader key={index} style={{ fontFamily: fontFamily }}>
                                    {column.render('Header')}
                                </StaticTableHeader>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                    {page.map((row, pageIndex) => {
                        prepareRow(row)
                        return (
                            <StaticTableRow key={pageIndex} {...row.getRowProps()}>
                                {row.cells.map((cell, rowIndex) => {
                                    return (
                                        <StaticTableData
                                            key={rowIndex}
                                            {...cell.getCellProps()}
                                            style={{
                                                fontFamily: fontFamily,
                                                borderBottom:
                                                    pageIndex == tableData.length - 1 ? '0px' : '0.5px solid #6C6C6C',
                                            }}
                                        >
                                            {cell.render('Cell')}
                                        </StaticTableData>
                                    )
                                })}
                            </StaticTableRow>
                        )
                    })}
                </tbody>
            </ConditionalRender>
        </table>
    )
}

export default FStaticTable
